import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import notesService, { type CreateNoteData, type UpdateNoteData } from '@/services/notes.service';

export const NOTES_QUERY_KEYS = {
  all: ['notes'] as const,
  byTicket: (ticketId: string) => ['notes', 'ticket', ticketId] as const,
  search: (params: any) => ['notes', 'search', params] as const,
};

export function useNotesByTicket(ticketId: string) {
  return useQuery({
    queryKey: NOTES_QUERY_KEYS.byTicket(ticketId),
    queryFn: () => notesService.getNotesByTicket(ticketId),
    enabled: !!ticketId,
    staleTime: 0, // Always fetch fresh data
    refetchOnWindowFocus: true,
  });
}

export function useCreateNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateNoteData) => notesService.createNote(data),
    onSuccess: (response, variables) => {
      // Invalidate and refetch notes for this ticket
      queryClient.invalidateQueries({
        queryKey: NOTES_QUERY_KEYS.byTicket(variables.ticket_id)
      });
      
      // Optionally add the new note to the cache immediately
      queryClient.setQueryData(
        NOTES_QUERY_KEYS.byTicket(variables.ticket_id),
        (oldData: any) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              notes: [...oldData.data.notes, response.data.note],
              stats: {
                ...oldData.data.stats,
                totalNotes: oldData.data.stats.totalNotes + 1
              }
            }
          };
        }
      );
    },
    onError: (error) => {
      console.error('Failed to create note:', error);
    }
  });
}

export function useUpdateNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateNoteData }) => 
      notesService.updateNote(id, data),
    onSuccess: (response, variables) => {
      // Update the note in all relevant queries
      queryClient.setQueriesData(
        { queryKey: NOTES_QUERY_KEYS.all },
        (oldData: any) => {
          if (!oldData?.data?.notes) return oldData;
          
          return {
            ...oldData,
            data: {
              ...oldData.data,
              notes: oldData.data.notes.map((note: any) => 
                note.id === variables.id ? response.data.note : note
              )
            }
          };
        }
      );
    },
    onError: (error) => {
      console.error('Failed to update note:', error);
    }
  });
}

export function useDeleteNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (noteId: string) => notesService.deleteNote(noteId),
    onSuccess: (_, noteId) => {
      // Remove the note from all relevant queries
      queryClient.setQueriesData(
        { queryKey: NOTES_QUERY_KEYS.all },
        (oldData: any) => {
          if (!oldData?.data?.notes) return oldData;
          
          const filteredNotes = oldData.data.notes.filter(
            (note: any) => note.id !== noteId
          );
          
          return {
            ...oldData,
            data: {
              ...oldData.data,
              notes: filteredNotes,
              stats: {
                ...oldData.data.stats,
                totalNotes: filteredNotes.length
              }
            }
          };
        }
      );
    },
    onError: (error) => {
      console.error('Failed to delete note:', error);
    }
  });
}

export function useSearchNotes(params: {
  q?: string;
  ticket_id?: string;
  author_id?: string;
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: NOTES_QUERY_KEYS.search(params),
    queryFn: () => notesService.searchNotes(params),
    enabled: Object.values(params).some(value => value !== undefined && value !== ''),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}